import { useState, useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

const navLinks: { label: string; href: string; hot?: boolean }[] = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Payment', href: '/payment' },
  { label: 'Slots', href: 'https://pnggo.bet/RFLANDINGPAGEPNG' },
  { label: 'Guidelines', href: '/guidelines' },
  { label: 'Promotion', href: 'https://pnggo.bet/RFLANDINGPAGEPNG', hot: true },
];

/** Red “breathing” dot — ping ripple + solid centre for Promotion callouts */
function PromotionHotDot() {
  return (
    <span className="relative ml-1 inline-flex h-2.5 w-2.5 shrink-0 align-middle" aria-hidden="true">
      <span className="absolute inline-flex h-full w-full motion-reduce:animate-none animate-ping rounded-full bg-[#B80E28] opacity-60" />
      <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#B80E28] shadow-[0_0_8px_rgba(184,14,40,0.85)] ring-2 ring-[#B80E28]/30" />
    </span>
  );
}

function isExternalHref(href: string) {
  return /^https?:\/\//i.test(href);
}

function NavAnchor({
  href,
  className,
  children,
  onClick,
}: {
  href: string;
  className: string;
  children: ReactNode;
  onClick?: () => void;
}) {
  if (isExternalHref(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
        onClick={onClick}
      >
        {children}
      </a>
    );
  }
  return (
    <Link to={href} className={className} onClick={onClick}>
      {children}
    </Link>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 border-b-2 border-[#B80E28] ${
        scrolled ? 'bg-gray-200 shadow-md' : 'bg-gray-200'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16">
        {/* Logo */}
        <Link to="/" className="flex-shrink-0">
          <img
            src="https://storage.readdy-site.link/project_files/c667a3fe-afd8-41e2-b509-06b17ff5eb78/1897808b-7c64-4faf-bdbc-437d72777f15_Image_20260417164818_372_17.png?v=7b4ee61be8e2cd668f2946d39c7e633c"
            alt="PNGGO Logo"
            className="h-10 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => (
            <NavAnchor
              key={link.label}
              href={link.href}
              className="inline-flex items-center text-black/80 hover:text-[#B80E28] text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer"
            >
              {link.label}
              {link.hot ? (
                <>
                  <PromotionHotDot />
                  <span className="sr-only"> — popular offers</span>
                </>
              ) : null}
            </NavAnchor>
          ))}
        </nav>

        {/* Sign Up Button */}
        <NavAnchor
          href="https://pnggo.bet/RFLANDINGPAGEPNG"
          className="hidden md:inline-flex items-center gap-2 bg-[#B80E28] text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-[#9a0b21] transition-colors whitespace-nowrap cursor-pointer"
        >
          Sign Up <i className="fas fa-chevron-right text-xs"></i>
        </NavAnchor>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden text-black text-xl cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <i className={menuOpen ? 'fas fa-times' : 'fas fa-bars'}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-gray-200 border-t border-white/20 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <NavAnchor
              key={link.label}
              href={link.href}
              className="inline-flex items-center text-black/80 hover:text-[#B80E28] text-sm font-medium transition-colors cursor-pointer"
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
              {link.hot ? (
                <>
                  <PromotionHotDot />
                  <span className="sr-only"> — popular offers</span>
                </>
              ) : null}
            </NavAnchor>
          ))}
          <NavAnchor
            href="https://pnggo.bet/RFLANDINGPAGEPNG"
            className="inline-flex items-center gap-2 bg-[#B80E28] text-white text-sm font-semibold px-5 py-2 rounded-full w-fit whitespace-nowrap cursor-pointer"
            onClick={() => setMenuOpen(false)}
          >
            Sign Up <i className="fas fa-chevron-right text-xs"></i>
          </NavAnchor>
        </div>
      )}
    </header>
  );
}
